import React, { useEffect, useState } from 'react'
import { useText } from '../context/TextContext'
import { useNavigate, useParams } from 'react-router-dom'
import { EquipmentItem, EquipmentCategory } from '../models/shared/EquipmentItem'
import { FormControl, FormGroup, FormLabel, Form } from 'react-bootstrap'
import PageHeader from '../components/PageHeader'
import { useForm } from 'react-hook-form'
import { AdvancedButton } from '../components/AdvancedButton'
import { useFirestore } from 'reactfire'
import { addDoc, collection, doc, setDoc } from 'firebase/firestore'
import { COLLECTIONS } from '../utils/shared/constants'
import { useOrganization } from '../context/OrganizationContext'
import { toast } from 'react-toastify'
import { useFirebaseDoc } from '../hooks/firebaseHooks'

interface Input {
    name: string
    category: EquipmentCategory
    make: string
    model: string
    serialNumber: string
    purchaseDate: string
    purchasePrice: string
    currentValue: string
    notes: string
}

const CATEGORIES: EquipmentCategory[] = [
    'chainsaw', 'climbing_gear', 'truck', 'chipper', 'rigging', 'hand_tools', 'other'
]

export default function EquipmentWritePage() {
    const text = useText()
    const { orgId } = useOrganization()
    const { itemId } = useParams<{ itemId: string }>()
    const { register, handleSubmit, setValue } = useForm<Input>({
        defaultValues: { category: 'other' }
    })
    const navigate = useNavigate()
    const [processing, setProcessing] = useState(false)
    const [loading, setLoading] = useState(!!itemId)
    const firestore = useFirestore()
    const [item] = useFirebaseDoc<EquipmentItem>(
        `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.EQUIPMENT}/${itemId}`
    )

    useEffect(() => {
        if (!item) return
        setValue('name', item.name)
        setValue('category', item.category)
        setValue('make', item.make ?? '')
        setValue('model', item.model ?? '')
        setValue('serialNumber', item.serialNumber ?? '')
        setValue('purchaseDate', item.purchaseDate ?? '')
        setValue('purchasePrice', item.purchasePrice != null ? item.purchasePrice.toString() : '')
        setValue('currentValue', item.currentValue != null ? item.currentValue.toString() : '')
        setValue('notes', item.notes ?? '')
        setLoading(false)
    }, [item, setValue])

    const categoryLabel = (cat: EquipmentCategory): string => {
        const map: Record<EquipmentCategory, string> = {
            chainsaw: text.equipmentCategoryChainsaw,
            climbing_gear: text.equipmentCategoryClimbingGear,
            truck: text.equipmentCategoryTruck,
            chipper: text.equipmentCategoryChipper,
            rigging: text.equipmentCategoryRigging,
            hand_tools: text.equipmentCategoryHandTools,
            other: text.equipmentCategoryOther,
        }
        return map[cat]
    }

    const onSubmit = async (data: Input) => {
        setProcessing(true)
        const newItem: EquipmentItem = {
            name: data.name,
            category: data.category,
            status: 'active',
            ...(data.make ? { make: data.make } : {}),
            ...(data.model ? { model: data.model } : {}),
            ...(data.serialNumber ? { serialNumber: data.serialNumber } : {}),
            ...(data.purchaseDate ? { purchaseDate: data.purchaseDate } : {}),
            ...(data.purchasePrice ? { purchasePrice: parseFloat(data.purchasePrice) } : {}),
            ...(data.currentValue ? { currentValue: parseFloat(data.currentValue) } : {}),
            ...(data.notes ? { notes: data.notes } : {}),
        }

        try {
            const colPath = `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.EQUIPMENT}`
            if (itemId) {
                await setDoc(doc(firestore, colPath, itemId), newItem, { merge: true })
            } else {
                await addDoc(collection(firestore, colPath), newItem)
            }
            navigate(-1)
        } catch (error) {
            console.error(error)
            toast.error(text.defaultError)
        }

        setProcessing(false)
    }

    return (
        <>
            <PageHeader showBackButton title={itemId ? text.editEquipment : text.newEquipment} />

            {loading ? null : (
                <Form className='d-flex flex-column gap-4' onSubmit={handleSubmit(onSubmit)}>
                    <FormGroup>
                        <FormLabel>{text.name}</FormLabel>
                        <FormControl {...register('name')} required />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{text.equipmentCategory}</FormLabel>
                        <FormControl as='select' {...register('category')} required>
                            {CATEGORIES.map(cat => (
                                <option key={cat} value={cat}>{categoryLabel(cat)}</option>
                            ))}
                        </FormControl>
                    </FormGroup>

                    <div className='row g-3'>
                        <FormGroup className='col-md-6'>
                            <FormLabel>{text.equipmentMake}</FormLabel>
                            <FormControl {...register('make')} />
                        </FormGroup>
                        <FormGroup className='col-md-6'>
                            <FormLabel>{text.equipmentModel}</FormLabel>
                            <FormControl {...register('model')} />
                        </FormGroup>
                    </div>

                    <FormGroup>
                        <FormLabel>{text.equipmentSerialNumber}</FormLabel>
                        <FormControl {...register('serialNumber')} />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{text.equipmentPurchaseDate}</FormLabel>
                        <FormControl type='date' {...register('purchaseDate')} />
                    </FormGroup>

                    <div className='row g-3'>
                        <FormGroup className='col-md-6'>
                            <FormLabel>{text.equipmentPurchasePrice}</FormLabel>
                            <FormControl type='number' step='0.01' min='0' {...register('purchasePrice')} />
                        </FormGroup>
                        <FormGroup className='col-md-6'>
                            <FormLabel>{text.equipmentCurrentValue}</FormLabel>
                            <FormControl type='number' step='0.01' min='0' {...register('currentValue')} />
                        </FormGroup>
                    </div>

                    <FormGroup>
                        <FormLabel>{text.equipmentNotes}</FormLabel>
                        <FormControl as='textarea' rows={3} {...register('notes')} />
                    </FormGroup>

                    <div className='ms-auto d-flex gap-4'>
                        <AdvancedButton icon='bi-x' variant='secondary' onClick={() => navigate(-1)}>
                            {text.cancel}
                        </AdvancedButton>
                        <AdvancedButton processing={processing} type='submit' icon='bi-floppy'>
                            {itemId ? text.save : text.create}
                        </AdvancedButton>
                    </div>
                </Form>
            )}
        </>
    )
}
